import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { Campo, Seletor } from './campos';
import { Modal } from './Modal';

const PAPEIS = [
  { valor: 'ASSISTENTE', rotulo: 'Assistente' },
  { valor: 'ADVOGADA', rotulo: 'Advogada' },
];

const EM_BRANCO = { nome: '', login: '', email: '', senha: '' };

/**
 * Cadastro de colaboradora.
 *
 * O botão "Nova colaboradora" existia na tela de Equipe desde sempre e não fazia
 * nada: nem `onClick`, nem diálogo. A rota `POST /usuarios` estava pronta o tempo
 * todo — era a tela que faltava. Mesma história do "Firmar acordo".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  A SENHA É DEFINIDA AQUI, E ISSO PRECISA SER DITO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Não há convite por e-mail neste sistema. Quem cadastra escolhe a senha inicial e
 * a entrega à colaboradora, que a troca no primeiro acesso. O diálogo diz isso em
 * voz alta — senha combinada e não repassada é acesso que ninguém consegue usar, e
 * a pessoa some da tela sem explicação.
 *
 * O LOGIN não é e-mail obrigatório: "bianca" funciona, e é como a equipe entra.
 */
export function ModalNovaColaboradora({
  aberto,
  aoFechar,
}: {
  aberto: boolean;
  aoFechar: () => void;
}) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const clienteConsulta = useQueryClient();
  const [dados, setDados] = useState(EM_BRANCO);
  const [papel, setPapel] = useState('ASSISTENTE');
  const [erro, setErro] = useState<string | null>(null);
  const [invalidos, setInvalidos] = useState<Record<string, string[]>>({});

  useEffect(() => {
    setDados(EM_BRANCO);
    setPapel('ASSISTENTE');
    setErro(null);
    setInvalidos({});
  }, [aberto]);

  function definir(campo: keyof typeof dados, valor: string) {
    setDados((atual) => ({ ...atual, [campo]: valor }));
    setInvalidos((atual) => {
      if (!atual[campo]) return atual;
      const { [campo]: _corrigido, ...resto } = atual;
      return resto;
    });
  }

  const criar = useMutation({
    mutationFn: () =>
      api.criar<{ id: string }>('/usuarios', {
        nome: dados.nome,
        login: dados.login,
        ...(dados.email.trim() ? { email: dados.email.trim() } : {}),
        senha: dados.senha,
        papel,
      }),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['equipe'] });
      aoFechar();
    },
    onError: (e) => {
      if (e instanceof FalhaDaApi) {
        setErro(e.message);
        setInvalidos(e.camposInvalidos ?? {});
        return;
      }
      setErro('Não foi possível cadastrar a colaboradora.');
    },
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const faltando: Record<string, string[]> = {};
    if (dados.nome.trim().length < 2) faltando['nome'] = ['Informe o nome.'];
    if (dados.login.trim().length < 3) faltando['login'] = ['Informe o login.'];
    if (dados.senha.length < 8) {
      faltando['senha'] = ['A senha deve ter ao menos 8 caracteres.'];
    }
    setInvalidos(faltando);
    if (Object.keys(faltando).length > 0) {
      setErro(null);
      return;
    }
    criar.mutate();
  }

  const erroDe = (campo: string) => {
    const mensagem = invalidos[campo]?.[0];
    return mensagem ? { erro: mensagem } : {};
  };

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Nova colaboradora"
      descricao="Acesso ao sistema, com registro de autoria em tudo o que ela fizer."
      largura="32rem"
      rodape={
        <>
          <Botao onClick={aoFechar} desabilitado={criar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={criar.isPending}
          >
            {criar.isPending ? 'Cadastrando…' : 'Cadastrar'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-4">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        <Campo
          rotulo="Nome"
          valor={dados.nome}
          aoMudar={(v) => definir('nome', v)}
          placeholder="Dra. Fulana de Tal"
          {...erroDe('nome')}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Login"
            valor={dados.login}
            aoMudar={(v) => definir('login', v)}
            placeholder="fulana"
            auxilio="Como ela entra. Não precisa ser e-mail."
            {...erroDe('login')}
          />
          <Seletor rotulo="Papel" valor={papel} aoMudar={setPapel} opcoes={PAPEIS} />
        </div>

        <Campo
          rotulo="E-mail (opcional)"
          tipo="email"
          valor={dados.email}
          aoMudar={(v) => definir('email', v)}
          {...erroDe('email')}
        />

        <Campo
          rotulo="Senha inicial"
          tipo="password"
          valor={dados.senha}
          aoMudar={(v) => definir('senha', v)}
          auxilio="Ao menos 8 caracteres, com letra e número."
          {...erroDe('senha')}
        />

        <p className="border-t pt-4 text-xs text-[color:var(--texto-tenue)]">
          Não há convite por e-mail: <strong>entregue esta senha a ela</strong>, que a
          troca no primeiro acesso. Todas têm acesso total — o login individual existe
          para o histórico registrar quem fez cada baixa e cada mudança de fase.
        </p>
      </form>
    </Modal>
  );
}
